import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlatformApiError, type EnquiryDetail, type EnquiryPage, type Viewer } from '@sfc/sdk';
import { platform } from '../api/client';
import { DetailPane, Split } from '../ui/DetailPane';
import { Empty } from '../ui/Empty';
import { dateTime, initials, relativeTime } from '../ui/format';
import { Icon } from '../ui/Icon';
import { SearchBox } from '../ui/SearchBox';
import { StatStrip } from '../ui/StatStrip';

const KINDS = [
  'studio_inquiry',
  'general_enquiry',
  'idea_holder_signup',
  'contributor_signup',
] as const;

const KIND_LABELS: Record<string, string> = {
  studio_inquiry: 'Studio enquiry',
  general_enquiry: 'General enquiry',
  idea_holder_signup: 'Idea holder',
  contributor_signup: 'Contributor',
};

/**
 * The answers the two community forms carry, as filters.
 *
 * Mirrors ENQUIRY_DETAIL_FILTERS on the API, which is what the request is
 * checked against. Only those forms ask these, so narrowing by one also
 * narrows to the form that asks it.
 */
const DETAIL_FILTERS = [
  {
    field: 'stage' as const,
    label: 'Stage',
    options: [
      ['idea', 'Idea'],
      ['validating', 'Validating'],
      ['prototype', 'Prototype'],
      ['launched', 'Launched'],
    ],
  },
  {
    field: 'compensationType' as const,
    label: 'Compensation',
    options: [
      ['equity', 'Equity'],
      ['paid', 'Paid'],
      ['hybrid', 'Hybrid'],
      ['portfolio', 'Portfolio'],
    ],
  },
  {
    field: 'availability' as const,
    label: 'Availability',
    options: [
      ['weekends', 'Weekends'],
      ['part_time', 'Part time'],
      ['full_time', 'Full time'],
    ],
  },
  {
    field: 'collaborationType' as const,
    label: 'Collaboration',
    options: [
      ['cofounder_equity', 'Cofounder, equity'],
      ['paid_freelance', 'Paid freelance'],
      ['portfolio', 'Portfolio'],
      ['open', 'Open'],
    ],
  },
] satisfies readonly { field: string; label: string; options: readonly (readonly [string, string])[] }[];

interface Filters {
  kind: string;
  status: string;
  stage: string;
  compensationType: string;
  availability: string;
  collaborationType: string;
}

const NO_FILTERS: Filters = {
  kind: '',
  status: '',
  stage: '',
  compensationType: '',
  availability: '',
  collaborationType: '',
};

/** Only what was actually chosen. An empty select is not a filter. */
function asQuery(filters: Filters): Record<string, string> {
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ''));
}

/**
 * The website's form submissions, in the order they arrived.
 *
 * Read-only apart from marking one handled: a lead is a message somebody sent,
 * not a record staff edit. The submitted fields are shown as text -- React
 * escapes them, and nothing here turns one into markup.
 */
export function EnquiriesPanel({ viewer }: { viewer: Viewer }): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const [page, setPage] = useState<EnquiryPage>();
  const [detail, setDetail] = useState<EnquiryDetail>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [query, setQuery] = useState('');
  /**
   * The cursors that were used to reach the page on screen.
   *
   * Keyset paging only knows how to go forward: a cursor says "everything
   * older than this", and there is no cursor for "the page I was just on". So
   * the way back is remembered rather than asked for. Empty means the first
   * page; one entry means the second, and stepping back is dropping the last
   * one. Exact, because it returns to the same boundary the reader came from
   * rather than to an offset that may have shifted underneath them.
   */
  const [trail, setTrail] = useState<string[]>([]);
  const detailRequest = useRef<AbortController | null>(null);

  const load = useCallback(
    async (cursor: string | undefined, applied: Filters, signal?: AbortSignal) => {
      setBusy(true);
      setProblem(undefined);
      try {
        setPage(
          await platform.enquiries.list(
            organizationId,
            { cursor, ...asQuery(applied) },
            signal,
          ),
        );
      } catch (error) {
        if (!signal?.aborted) setProblem(describe(error));
      } finally {
        if (!signal?.aborted) setBusy(false);
      }
    },
    [organizationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setPage(undefined);
    setDetail(undefined);
    setTrail([]);
    void load(undefined, NO_FILTERS, controller.signal);
    return () => {
      controller.abort();
      detailRequest.current?.abort();
    };
  }, [load]);

  /** Any filter change starts again at the newest, because the old trail led through a different inbox. */
  function narrow(change: Partial<Filters>): void {
    const next = { ...filters, ...change };
    setFilters(next);
    setTrail([]);
    setDetail(undefined);
    void load(undefined, next);
  }

  function older(): void {
    const cursor = page?.nextCursor;
    if (!cursor) return;
    setTrail([...trail, cursor]);
    setDetail(undefined);
    void load(cursor, filters);
  }

  function newer(): void {
    if (trail.length === 0) return;
    const shorter = trail.slice(0, -1);
    setTrail(shorter);
    setDetail(undefined);
    void load(shorter.at(-1), filters);
  }

  /**
   * The inbox, not the page of it being looked at.
   *
   * These used to count the rows that happened to be loaded, which is the page
   * size until the last page: an inbox of thirty-six read as twenty-five, and
   * the rest were invisible to anybody who did not think to page forward. The
   * API sends the real counts alongside the page now.
   */
  const stats = useMemo(() => {
    const counts = page?.counts;
    const awaiting = counts?.new ?? 0;
    return [
      { label: 'Submissions', value: counts?.total ?? 0 },
      { label: 'Awaiting an answer', value: awaiting, tone: awaiting > 0 ? ('down' as const) : undefined },
      { label: 'Handled', value: counts?.handled ?? 0 },
      { label: 'On this page', value: page?.items.length ?? 0 },
    ];
  }, [page]);

  async function open(id: string) {
    detailRequest.current?.abort();
    const controller = new AbortController();
    detailRequest.current = controller;
    setDetail(undefined);
    setProblem(undefined);
    try {
      setDetail(await platform.enquiries.detail(organizationId, id, controller.signal));
    } catch (error) {
      if (!controller.signal.aborted) setProblem(describe(error));
    }
  }

  async function mark(id: string, next: 'new' | 'handled') {
    setProblem(undefined);
    try {
      await platform.enquiries.setStatus(organizationId, id, next);
      await open(id);
      await load(trail.at(-1), filters);
    } catch (error) {
      setProblem(describe(error));
    }
  }

  const needle = query.trim().toLowerCase();
  const shown = (page?.items ?? []).filter(
    (entry) => !needle || `${entry.name} ${entry.email}`.toLowerCase().includes(needle),
  );

  return (
    <section className="card" aria-labelledby="section-heading">
      <div className="toolbar">
        <SearchBox label="Search submissions" placeholder="Search this page by name or email" value={query} onChange={setQuery} />
        <select
          className="select"
          aria-label="Filter by form"
          data-testid="kind-filter"
          value={filters.kind}
          onChange={(event) => narrow({ kind: event.target.value })}
        >
          <option value="">All forms</option>
          {KINDS.map((value) => (
            <option key={value} value={value}>
              {KIND_LABELS[value]}
            </option>
          ))}
        </select>
        <select
          className="select"
          aria-label="Filter by submission status"
          data-testid="enquiry-status-filter"
          value={filters.status}
          onChange={(event) => narrow({ status: event.target.value })}
        >
          <option value="">All statuses</option>
          <option value="new">new</option>
          <option value="handled">handled</option>
        </select>

        {DETAIL_FILTERS.map(({ field, label, options }) => (
          <select
            key={field}
            className="select"
            aria-label={`Filter by ${label.toLowerCase()}`}
            data-testid={`${field}-filter`}
            value={filters[field]}
            onChange={(event) => narrow({ [field]: event.target.value })}
          >
            <option value="">Any {label.toLowerCase()}</option>
            {options.map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </select>
        ))}

        <span className="toolbar__spacer" />
        <button className="button" disabled={busy} onClick={() => void load(trail.at(-1), filters)}>
          <Icon name="refresh" size={16} />
          Refresh submissions
        </button>
      </div>

      <StatStrip stats={stats} testId="enquiry-stats" />

      {problem ? (
        <p className="notice notice--problem" role="alert">
          {problem}
        </p>
      ) : null}
      {busy && !page ? (
        <p className="loading" role="status">
          Loading submissions…
        </p>
      ) : null}

      <Split open={Boolean(detail)}>
        <div className="split__list">
          {page?.items.length === 0 ? (
            <Empty
              icon="submissions"
              title="No submissions yet"
              hint="Messages from the contact and community forms arrive here."
            />
          ) : null}
          {page && page.items.length > 0 && shown.length === 0 ? (
            <Empty icon="search" title="Nothing on this page matches" hint="Clear the search, or load older submissions." />
          ) : null}

          <ul className="ledger" aria-label="Submissions inbox">
            {shown.map((entry) => (
              <li
                key={entry.id}
                className={`ledger__row ledger__row--inbox${detail?.id === entry.id ? ' ledger__row--selected' : ''}${
                  entry.status === 'new' ? ' ledger__row--unread' : ''
                }`}
                onClick={() => void open(entry.id)}
              >
                <span className="avatar" aria-hidden="true">
                  {initials(entry.name)}
                </span>
                <div className="ledger__id">
                  <span className="row__title">
                    {KIND_LABELS[entry.kind] ?? entry.kind} · {entry.name}
                  </span>
                  <span className="row__sub">{entry.email}</span>
                </div>
                <div className="ledger__meta">
                  <span
                    className={entry.status === 'handled' ? 'pill pill--up' : 'pill pill--attention'}
                    data-testid={`enquiry-status-${entry.id}`}
                  >
                    {entry.status}
                  </span>
                  <time className="row__sub" dateTime={entry.createdAt} title={dateTime(entry.createdAt)}>
                    {relativeTime(entry.createdAt)}
                  </time>
                </div>
                <button
                  className="button button--small"
                  aria-label={`Read submission from ${entry.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void open(entry.id);
                  }}
                >
                  Read
                </button>
              </li>
            ))}
          </ul>

          {/* Both ways. Forward-only paging is a one-way door: a reader who
              steps past what they were looking for has to start again from the
              newest and walk back down. */}
          {trail.length > 0 || page?.nextCursor ? (
            <div className="pager-row">
              <button
                className="button pager"
                disabled={busy || trail.length === 0}
                onClick={newer}
                data-testid="newer-page"
              >
                Newer submissions
              </button>
              <span className="pager__where" data-testid="page-position">
                Page {trail.length + 1}
              </span>
              <button
                className="button pager"
                disabled={busy || !page?.nextCursor}
                onClick={older}
                data-testid="older-page"
              >
                Older submissions
              </button>
            </div>
          ) : null}
        </div>

        {detail ? (
          <DetailPane
            label="Submission details"
            recordKey={detail.id}
            title={detail.name}
            subtitle={
              <>
                {KIND_LABELS[detail.kind] ?? detail.kind},{' '}
                <time dateTime={detail.createdAt} title={dateTime(detail.createdAt)}>
                  {relativeTime(detail.createdAt).toLowerCase()}
                </time>
              </>
            }
            closeLabel="Close details"
            onClose={() => setDetail(undefined)}
          >
            <div className="detail__actions">
              <span className={detail.status === 'handled' ? 'pill pill--up' : 'pill pill--attention'}>
                <span data-testid="enquiry-detail-status">{detail.status}</span>
              </span>
              {detail.status === 'handled' ? (
                <button className="button button--small" onClick={() => void mark(detail.id, 'new')}>
                  Mark as new
                </button>
              ) : (
                <button className="button button--primary button--small" onClick={() => void mark(detail.id, 'handled')}>
                  Mark handled
                </button>
              )}
            </div>
            <dl className="facts">
              <div>
                <dt>Form</dt>
                <dd data-testid="enquiry-kind">{KIND_LABELS[detail.kind] ?? detail.kind}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${detail.email}`}>{detail.email}</a>
                </dd>
              </div>
              <div>
                <dt>Received</dt>
                <dd>
                  <time dateTime={detail.createdAt}>{dateTime(detail.createdAt)}</time>
                </dd>
              </div>
              {Object.entries(detail.details).map(([field, value]) => (
                <div key={field} className={value.length > 40 ? 'facts__wide' : undefined}>
                  <dt>{label(field)}</dt>
                  <dd className={value.length > 40 ? 'letter' : undefined}>{value || 'Not provided'}</dd>
                </div>
              ))}
            </dl>
          </DetailPane>
        ) : null}
      </Split>
    </section>
  );
}

/** `projectDetails` reads as `Project details` in a definition list. */
function label(field: string): string {
  const words = field.replace(/([A-Z])/g, ' $1').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function describe(error: unknown): string {
  return error instanceof PlatformApiError
    ? error.message
    : 'The submissions could not be loaded. Please try again.';
}
